import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SolEstimatePricingMode, SolMaterialEscalationType, SolLaborEscalationType, MaterialSubCategory } from '../../models/enum';
import { EscalationData as EscalationDataDTO, Escalations, EstimateTab, EstimateGrossMargin, SQWebProject, CostReviewStatus } from '../../models/dtos';
import { CONSTANTS } from '../../Constants/Constants';
import { SellPriceConfig, TaxTypes } from '../../models/sol-estimate-table/SellPriceConfig';
import { ShippingHandlingData } from '../../models/sol-estimate-table/ShippingHandlingData';
import { WarrantyData } from '../../models/sol-estimate-table/WarrantyData';
import { SolMaterialEscalationDialogDataModel } from '../../models/sol-estimate-table/SolMaterialEscalationDialogDataModel';
import { SolEstimateRiskData } from '../../models/sol-estimate-table/SolEstimateRiskData';
import { CommonDataService } from '../common-data.service';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import { SolEstimateMaterialService } from './sol-estimate-material.service';
import { SolEstimateLaborService } from './sol-estimate-labor.service';
import { SolEstimateSubcontractorService } from './sol-estimate-subcontractor.service';
import { SolEstimateMiscRiskTravelService } from './sol-estimate-misc-risk-travel.service';
import { SolEstimateTaxService } from './sol-estimate-tax.service';
import { SolEstimateTechnologyFeeService } from './sol-estimate-technology-fee.service';
import { SolEstimateSubtotalsService } from './sol-estimate-subtotals.service';
import { SolEstimateSellAsService } from './sol-estimate-sell-as.service';
import { SolEstimateDtoSyncService } from './sol-estimate-dto-update.service';
import { CostReviewRecordBuilderService } from '../review/cost-review-record-builder.service';
import {
  EstimateUiModel,
  EstimateLaborCostsModel,
  EstimateRowModel,
  EstimateMaterialTypeModel,
  createDefaultEstimateUiModel,
} from '../../models/sol-estimate-table/ui-models/index';

/**
 * Orchestrator service for the estimate table.
 *
 * Owns:
 *  - The EstimateUiModel (plain data model � the single source of truth for calculations)
 *  - The current SellPriceConfig
 *  - DTO synchronisation (writing model state back to DTOs)
 *
 * Does NOT own:
 *  - FormGroup creation or patching (that responsibility belongs to the component)
 *
 * Delegates to section-specific services:
 *  - EstimateCommonFunctionsService      -- shared calculations, formatting
 *  - SolEstimateMaterialService          -- material costs section
 *  - SolEstimateLaborService             -- labor costs section
 *  - SolEstimateSubcontractorService     -- subcontractor costs section
 *  - SolEstimateMiscRiskTravelService    -- misc/risk/travel costs section
 *  - SolEstimateTaxService               -- tax costs section
 *  - SolEstimateTechnologyFeeService     -- technology fee row
 *  - SolEstimateSubtotalsService         -- sub-totals row
 *  - SolEstimateSellAsService            -- sell-as section
 *  - SolEstimateDtoSyncService           -- DTO sync
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateService {

  /** The plain-data UI model — single source of truth. */
  estimateModel: EstimateUiModel = createDefaultEstimateUiModel();

  /** Emits the updated model whenever a full external recalculation completes (e.g. from cost calculation pipeline). */
  private readonly estimateModelUpdated$ = new Subject<EstimateUiModel>();

  /** Emits only the current sell-as sell price when that value changes. */
  private readonly sellAsSellPriceUpdated$ = new Subject<number>();

  /** Observable that components can subscribe to for model updates triggered outside the estimate component. */
  readonly onEstimateModelUpdated$ = this.estimateModelUpdated$.asObservable();

  /** Observable that consumers can subscribe to for sell-as sell-price updates only. */
  readonly onSellAsSellPriceUpdated$ = this.sellAsSellPriceUpdated$.asObservable();

  /**
   * Notifies subscribers that the estimate model has been updated externally.
   * Should only be called from orchestration services (e.g. SolCalculationsService)
   * after an external recalculation pipeline completes — NOT from normal
   * component-driven flows where the model is returned directly.
   */
  notifyModelUpdated(): void {
    this.estimateModelUpdated$.next(this.estimateModel);
    this.notifySellAsSellPriceUpdated(this.estimateModel?.sellAs?.sellAsSellPrice || 0);
  }

  notifySellAsSellPriceUpdated(sellAsSellPrice: number): void {
    this.sellAsSellPriceUpdated$.next(sellAsSellPrice);
  }

  /** Stored config so reactive handlers can reuse it without rebuilding from DTO. */
  private currentConfig!: SellPriceConfig;

  /** Set when a zero-cost Fixed Margin estimate moves to Line Item, so returning to Fixed Margin keeps its margin. */
  private keepsFixedMarginOnReturn = false;

  /** Tax row visibility flags delegated from SolEstimateTaxService. */
  get taxVisibility() {
    return this.taxService.taxVisibility;
  }

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
    private readonly materialService: SolEstimateMaterialService,
    private readonly laborService: SolEstimateLaborService,
    private readonly subcontractorService: SolEstimateSubcontractorService,
    private readonly miscRiskTravelService: SolEstimateMiscRiskTravelService,
    private readonly taxService: SolEstimateTaxService,
    private readonly technologyFeeService: SolEstimateTechnologyFeeService,
    private readonly subtotalsService: SolEstimateSubtotalsService,
    private readonly sellAsService: SolEstimateSellAsService,
    private readonly dtoSyncService: SolEstimateDtoSyncService,
    private readonly costReviewRecordBuilderService: CostReviewRecordBuilderService
  ) { }

  // -- Model Initialisation ------------------------------------------

  /** 
   * Resets the model to defaults and returns it.
   * Notifies all subscribers through onEstimateModelUpdated$ observable
   * so components can react to the reset (e.g., clear form bindings).
   */
  resetEstimateModel(): EstimateUiModel {
    this.estimateModel = createDefaultEstimateUiModel();
    this.currentConfig = undefined;
    this.keepsFixedMarginOnReturn = false;
    this.materialService.setModelReference(this.estimateModel);
    // Notify subscribers of the reset
    this.notifyModelUpdated();
    return this.estimateModel;
  }

  setShowRiskCostsAccordion(value: boolean): void {
    if (this.estimateModel.miscRiskTravelCosts.riskCosts) {
      this.estimateModel.miscRiskTravelCosts.riskCosts.showRiskCostsAccordion = value;
    }
  }

  /** Removes MECHANICAL_FEE row from the model when visibility conditions are not met. */
  private filterMechanicalFeeVisibility(): void {
    this.estimateModel.miscRiskTravelCosts.showMechanicalFee = this.estimateCommonFunctions.isMechanicalFeeApplicable();
  }

  // -- Model Patching (initial load) ---------------------------------

  /**
   * Populates the EstimateUiModel from DTO values.
   * Returns the updated model so the component can sync it to a FormGroup.
   */
  patchEstimateModelValues(estimateValues: EstimateTab): EstimateUiModel {
    if (!estimateValues) {
      return this.estimateModel;
    }
    const useAutoRisk = this.commonDataService.getEstimateMetaData()?.estimateSettings?.useAutoRisk ?? false;
    this.setShowRiskCostsAccordion(useAutoRisk);
    this.filterMechanicalFeeVisibility();
    this.buildSellPriceConfig(estimateValues);
    const config = this.currentConfig;
    this.estimateModel.markUpToggle = config.isMarkUpSelected;
    // TODO: subTotalsCost still taken from EstimateTab — no direct SQWebProject category equivalent
    const subTotalsCost = estimateValues.subTotalsCost;
    const sqWebProject: SQWebProject | undefined = this.commonDataService.getSQWebProject();

    this.materialService.patchMaterialCosts(this.estimateModel, config, subTotalsCost, sqWebProject);
    this.laborService.patchLaborCosts(this.estimateModel, config, subTotalsCost, sqWebProject);
    // TODO: Subcontractor costs still taken from EstimateTab — SQWebProject has no equivalent subcontractor line-item array
    this.subcontractorService.patchSubContractorCosts(this.estimateModel, estimateValues, config, subTotalsCost);
    this.taxService.patchTaxCosts(this.estimateModel, config, subTotalsCost, sqWebProject);
    this.miscRiskTravelService.patchMiscRiskTravelCosts(this.estimateModel, config, subTotalsCost, sqWebProject);
    // Sales tax is charged on sell prices, so it has to run again once the misc rows above have theirs.
    if (config.taxTypeID === TaxTypes.SalesTax) {
      this.taxService.patchTaxCosts(this.estimateModel, config, subTotalsCost, sqWebProject);
    }
    this.technologyFeeService.patchTechnologyFeeCost(this.estimateModel, estimateValues, config, subTotalsCost, sqWebProject);
    this.subtotalsService.patchSubTotalsCost(this.estimateModel, config);

    // Fixed Price mode: GM% is not persisted in the DTO, so we must derive
    // it from the persisted sell price before the normal flow overwrites it.
    // This mirrors the onSellAsSellPriceChanged interactive flow.
    if (this.currentConfig.isFixedPriceSelected === true) {
      // 1. Restore the persisted user-entered sell price from the DTO
      //    (recalculateSellAsSellPrice would overwrite it with a sum
      //    computed from wrong sell prices).
      this.estimateModel.sellAs.sellAsSellPrice = estimateValues.soldASSellPrice;

      // 2. Recalculate tech fee cost (depends on sellAsSellPrice) and
      //    subtotals so deriveFixedPriceGMPercent has correct inputs.
      this.technologyFeeService.recalculateTechnologyFeeRow(this.estimateModel, this.currentConfig);
      this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);

      // 3. Derive GM% from the persisted sell price + costs.
      const derivedPercent = this.calculateGrossMarginPercent();
      this.currentConfig = { ...this.currentConfig, soldAsGMPercent: derivedPercent };

      // 4. Recalculate all sections with the correct GM%.
      //    recalculateAllSections also calls syncModelStateToDTOs.
      this.recalculateAllSections();
      return this.estimateModel;
    }

    this.recalculateSellAsSellPriceAndNotify();

    // Sync calculated values back into the DTO models
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  //#region Shipping and Handling

  /**
   * Patches shipping costs into the model, recalculates material sell prices
   * and dependent sections, then syncs DTOs.
   * Called by the estimate component after the shipping dialog commits.
   */
  updateShippingData(shippingAndHandling: ShippingHandlingData[]): void {
    this.materialService.patchShippingData(shippingAndHandling);
    this.materialService.updateShippingDataToDTO(shippingAndHandling);
    this.materialService.calculateMaterialSellPrices(this.estimateModel, this.currentConfig);
    this.recalculateAfterCostChange();
    this.syncModelStateToDTOs();
  }

  //#endregion

  //#region Warranty

  /**
   * Patches warranty costs into the model, recalculates material sell prices
   * and dependent sections, then syncs DTOs.
   * Called by the estimate component after the warranty dialog commits.
   */
  updateWarrantyData(warrantyData: WarrantyData[], locked: boolean): void {
    this.materialService.patchWarrantyData(warrantyData);
    this.materialService.updateWarrantyDataToDTO(warrantyData, locked);
    this.materialService.calculateMaterialSellPrices(this.estimateModel, this.currentConfig);
    this.recalculateAfterCostChange();
    this.syncModelStateToDTOs();
  }

  //#endregion

  //#region Risk

  /**
   * Updates auto-risk row values after the Risk dialog commits.
   * Uses recalculateMiscRiskTravelSellPricesOnly to preserve the user-entered risk cost
   * (skips recalculateDynamicRowCosts which would overwrite it with the formula value).
   * Then recalculates technology fee, subtotals and sell-as, and syncs DTOs.
   */
  updateRiskData(riskData: SolEstimateRiskData): void {
    const riskRow = this.miscRiskTravelService.findNamedRow(this.estimateModel, 'CALCULATED_RISK_COSTS');
    if (!riskRow) {
      return;
    }

    riskRow.cost = riskData.dollars ?? CONSTANTS.ZERO;
    riskRow.tdcPercentage = riskData.tdcPercent ?? CONSTANTS.ZERO;

    // Recalculate sell prices without overwriting the risk cost with the formula value
    this.miscRiskTravelService.recalculateMiscRiskTravelSellPricesOnly(this.estimateModel, this.currentConfig);

    // Recalculate tax (which may depend on misc totals), then downstream sections
    this.taxService.recalculateTaxCosts(this.estimateModel, this.currentConfig);
    this.technologyFeeService.recalculateTechnologyFeeRow(this.estimateModel, this.currentConfig);
    this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);

    // Fixed Price pins the sell price, so the new risk cost changes the GM% and it has to be applied
    // to the rows above (material, labor, subcontractor, misc) as well.
    if (this.currentConfig.isFixedPriceSelected === true) {
      this.reapplyPinnedSellPriceMarginKeepingRisk();
    }
    this.recalculateSellAsSellPriceAndNotify();

    this.syncModelStateToDTOs();
  }

  /**
   * Same as reapplyPinnedSellPriceMargin, but re-prices the misc rows without recalculating their dynamic
   * costs, so the risk cost entered in the Risk dialog is not overwritten by the formula value.
   */
  private reapplyPinnedSellPriceMarginKeepingRisk(): void {
    const derivedPercent = this.calculateGrossMarginPercent();
    this.currentConfig = { ...this.currentConfig, soldAsGMPercent: derivedPercent };

    this.materialService.calculateMaterialSellPrices(this.estimateModel, this.currentConfig);
    this.laborService.recalculateLaborSellPrices(this.estimateModel, this.currentConfig);
    this.subcontractorService.recalculateSubContractorSellPrices(this.estimateModel, this.currentConfig);
    this.miscRiskTravelService.recalculateMiscRiskTravelSellPricesOnly(this.estimateModel, this.currentConfig);
    this.taxService.recalculateTaxCosts(this.estimateModel, this.currentConfig);
    this.technologyFeeService.recalculateTechnologyFeeRow(this.estimateModel, this.currentConfig);
    this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);
  }

  //#endregion

  //#region Escalation

  /**
   * Updates escalation data after the escalation dialog commits.
   * Identifies the specific material or labor category that was changed,
   * recalculates its escalated cost using the updated percentages/rates,
   * and updates the corresponding row in the estimate model.
   * If "apply to all" is checked, copies the escalation settings to all
   * categories of the same type.
   */
  updateEscalationData(escalationDialogResult: SolMaterialEscalationDialogDataModel): void {
    const escalationRates = this.commonDataService.getEstimateMetaData()?.escalationRates;
    if (!escalationRates) {
      return;
    }

    // Sync apply-to-all flags
    if (escalationDialogResult.escalationType === CONSTANTS.MATERIAL) {
      escalationRates.applyMaterialEscalationToAll = escalationDialogResult.applyMaterialEscalationToAll ?? false;
      this.updateMaterialEscalation(escalationDialogResult, escalationRates);
      this.materialService.calculateMaterialSellPrices(this.estimateModel, this.currentConfig);
    } else {
      escalationRates.applyLaborEscalationToAll = escalationDialogResult.applyLaborEscalationToAll ?? false;
      this.updateLaborEscalation(escalationDialogResult, escalationRates);
      this.laborService.recalculateLaborSellPrices(this.estimateModel, this.currentConfig);
    }

    this.recalculateAfterCostChange();
    this.syncModelStateToDTOs();
  }

  /**
   * Updates material category costs based on escalation dialog result.
   * Maps sectionLabel to the correct escalation data and model row,
   * then applies the escalation calculation.
   */
  private updateMaterialEscalation(result: SolMaterialEscalationDialogDataModel, escalationRates: Escalations): void {
    const materialMappings = this.getMaterialEscalationMappings(escalationRates);

    if (result.applyMaterialEscalationToAll) {
      // Copy percentages and enabled flag from the changed category to all others, then recalculate each
      for (const mapping of materialMappings) {
        this.copyEscalationSettings(result.escalationData, mapping.escalationData);
        this.applyEscalationToRow(mapping.escalationData, mapping.modelRow);
        this.materialService.recalculateShippingAndWarrantyForCategory(mapping.materialType, mapping.category);
      }
    } else {
      // Only update the specific category that was changed
      const mapping = materialMappings.find(m => m.sectionLabel === result.sectionLabel);
      if (mapping) {
        this.applyEscalationToRow(mapping.escalationData, mapping.modelRow);
        this.materialService.recalculateShippingAndWarrantyForCategory(mapping.materialType, mapping.category);
      }
    }
  }

  private getMaterialEscalationMappings(escalationRates: Escalations): Array<{
    sectionLabel: string;
    escalationData: EscalationDataDTO;
    modelRow: EstimateRowModel;
    materialType: EstimateMaterialTypeModel;
    category: MaterialSubCategory;
  }> {
    return [
      { sectionLabel: SolMaterialEscalationType.Siemens_Material, escalationData: escalationRates.siemensMaterialEscalation, modelRow: this.estimateModel.materialCosts.siemensMaterial.headerRow, materialType: this.estimateModel.materialCosts.siemensMaterial, category: MaterialSubCategory.Siemens },
      { sectionLabel: SolMaterialEscalationType.Outside_Material, escalationData: escalationRates.outsideMaterialEscalation, modelRow: this.estimateModel.materialCosts.outsideMaterial.headerRow, materialType: this.estimateModel.materialCosts.outsideMaterial, category: MaterialSubCategory.Outside },
      { sectionLabel: SolMaterialEscalationType.Mechanical_Material, escalationData: escalationRates.mechanicalMaterialEscalation, modelRow: this.estimateModel.materialCosts.mechanicalMaterial.headerRow, materialType: this.estimateModel.materialCosts.mechanicalMaterial, category: MaterialSubCategory.Mechanical },
      { sectionLabel: SolMaterialEscalationType.Electrical_Material, escalationData: escalationRates.electricalMaterialEscalation, modelRow: this.estimateModel.materialCosts.electricalMaterial.headerRow, materialType: this.estimateModel.materialCosts.electricalMaterial, category: MaterialSubCategory.Electrical },
    ];
  }

  /**
   * Updates labor category costs based on escalation dialog result.
   * Maps sectionLabel to the correct escalation data and model row,
   * then applies the escalation calculation.
   */
  private updateLaborEscalation(result: SolMaterialEscalationDialogDataModel, escalationRates: Escalations): void {
    const laborMappings = this.getLaborEscalationMappings(escalationRates);

    if (result.applyLaborEscalationToAll) {
      for (const mapping of laborMappings) {
        this.copyEscalationSettings(result.escalationData, mapping.escalationData);
        this.applyEscalationToRow(mapping.escalationData, mapping.modelRow);
      }
    } else {
      const mapping = laborMappings.find(m => m.sectionLabel === result.sectionLabel);
      if (mapping) {
        this.applyEscalationToRow(mapping.escalationData, mapping.modelRow);
      }
    }
  }

  private getLaborEscalationMappings(escalationRates: Escalations): Array<{
    sectionLabel: string;
    escalationData: EscalationDataDTO;
    modelRow: EstimateRowModel;
  }> {
    return [
      { sectionLabel: SolLaborEscalationType.Project_Management, escalationData: escalationRates.pmLaborEscalation, modelRow: this.estimateModel.laborCosts.projectManagement },
      { sectionLabel: SolLaborEscalationType.Engineering, escalationData: escalationRates.engLaborEscalation, modelRow: this.estimateModel.laborCosts.engineering },
      { sectionLabel: SolLaborEscalationType.NETWORK_ENGINEERING, escalationData: escalationRates.nengLaborEscalation, modelRow: this.estimateModel.laborCosts.networkEngineering },
      { sectionLabel: SolLaborEscalationType.QUALIFICATION_ENGINEERING, escalationData: escalationRates.qengLaborEscalation, modelRow: this.estimateModel.laborCosts.qualificationEngineering },
      { sectionLabel: SolLaborEscalationType.CYBER_SECURITY_ENGINEERING, escalationData: escalationRates.cengLaborEscalation, modelRow: this.estimateModel.laborCosts.cyberSecurityEngineering },
      { sectionLabel: SolLaborEscalationType.ENERGY_ENGINEERING, escalationData: escalationRates.eengLaborEscalation, modelRow: this.estimateModel.laborCosts.energyEngineering },
      { sectionLabel: SolLaborEscalationType.ELECTRICAL_ENGINEERING, escalationData: escalationRates.lengLaborEscalation, modelRow: this.estimateModel.laborCosts.electricalEngineering },
      { sectionLabel: SolLaborEscalationType.GRAPHICS, escalationData: escalationRates.grapLaborEscalation, modelRow: this.estimateModel.laborCosts.graphics },
      { sectionLabel: SolLaborEscalationType.PROGRAMMING, escalationData: escalationRates.progLaborEscalation, modelRow: this.estimateModel.laborCosts.programming },
      { sectionLabel: SolLaborEscalationType.SPECIALIST, escalationData: escalationRates.specLaborEscalation, modelRow: this.estimateModel.laborCosts.specialist },
      { sectionLabel: SolLaborEscalationType.DIGITAL_DEPLOYMENT_SPECIALIST, escalationData: escalationRates.ddepLaborEscalation, modelRow: this.estimateModel.laborCosts.digitalDeploymentSpecialist },
      { sectionLabel: SolLaborEscalationType.MECHANICAL, escalationData: escalationRates.mechLaborEscalation, modelRow: this.estimateModel.laborCosts.mechanical },
      { sectionLabel: SolLaborEscalationType.ELECTRICAL, escalationData: escalationRates.elecLaborEscalation, modelRow: this.estimateModel.laborCosts.electrical },
    ];
  }

  /**
   * Applies escalation to a single estimate row.
   * If escalation is enabled, calculates the escalated cost and sets it as the row cost.
   * If disabled, restores the base cost (un-escalated) to the row.
   */
  private applyEscalationToRow(escalationData: EscalationDataDTO, modelRow: EstimateRowModel): void {
    if (escalationData.escalationEnabled) {
      const escalatedCost = this.estimateCommonFunctions.calculateTotalEscalatedCost(escalationData);
      escalationData.escalatedCost = escalatedCost;
      modelRow.cost = escalatedCost;
    } else {
      escalationData.escalatedCost = CONSTANTS.ZERO;
      modelRow.cost = escalationData.baseCost || CONSTANTS.ZERO;
    }
    modelRow.escalate = escalationData.escalationEnabled ? CONSTANTS.Y : CONSTANTS.N;
  }

  /**
   * Copies escalation percentages and enabled flag from source to target.
   * Does NOT copy rates (secondYearRate, etc.) as those are category-specific from the backend.
   */
  private copyEscalationSettings(source: EscalationDataDTO, target: EscalationDataDTO): void {
    target.escalationEnabled = source.escalationEnabled;
    target.firstYearPercentage = source.firstYearPercentage;
    target.secondYearPercentage = source.secondYearPercentage;
    target.thirdYearPercentage = source.thirdYearPercentage;
    target.fourthYearPercentage = source.fourthYearPercentage;
    target.fifthYearPercentage = source.fifthYearPercentage;
  }


  //#endregion


  // -- Config Management ---------------------------------------------

  getCurrentConfig(): SellPriceConfig {
    return this.currentConfig;
  }

  /** Returns the current model (for component to read after service operations). */
  getModel(): EstimateUiModel {
    return this.estimateModel;
  }

  /**
   * Ensures the estimate model has been populated with calculated sell prices.
   * If the model has not yet been calculated (e.g. user hasn't navigated to the estimate tab),
   * this will run the calculation using the persisted EstimateTab DTO values.
   * Safe to call multiple times — subsequent calls are no-ops.
   * Current Use - If sell price to be calculated before navigating to the estimate tab
   */
  ensureModelPopulated(): void {
    if (!this.currentConfig) {
      const estimateMetaData = this.commonDataService.getEstimateMetaData();
      const estimateTab = estimateMetaData?.estimateTab ?? new EstimateTab();
      this.patchEstimateModelValues(estimateTab);
    }
  }

  private buildSellPriceConfig(estimateValues: EstimateTab): void {
    const gm: EstimateGrossMargin | undefined = this.commonDataService.getOtherMetaData()?.grossMargin;
    const soldAsGMPercent = gm?.soldASGMPercent ?? CONSTANTS.ZERO;
    const isLineItemMode = estimateValues.isFixedPriceSelected === null || estimateValues.isFixedPriceSelected === undefined;
    const estimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;

    this.currentConfig = {
      isMarkUpSelected: this.currentConfig?.isMarkUpSelected ?? estimateValues.isMarkUpSelected ?? false,
      soldAsGMPercent,
      isFixedPriceSelected: isLineItemMode ? null : estimateValues.isFixedPriceSelected,
      techFeePercent: this.resolveTechFeePercent(),
      technologyFeeGMPercent: gm?.technologyFeeGMPercent,
      taxTypeID: estimateSettings?.taxTypeID ?? TaxTypes.NoTax,
      isEstimateReadOnly: this.commonDataService.isReadOnlyMode()
    };

    this.taxService.updateTaxVisibility(this.currentConfig.taxTypeID);
  }

  /** Technology fee percentage is zero whenever the fee is switched off in Properties. */
  private resolveTechFeePercent(): number {
    const useTechFee = this.commonDataService.getEstimateMetaData()?.estimateSettings?.useTechFee ?? false;
    if (!useTechFee) {
      return CONSTANTS.ZERO;
    }
    return this.commonDataService.getFeesData()?.technologyFeePercentage ?? CONSTANTS.ZERO;
  }

  // -- DTO Sync (public) ---------------------------------------------

  /**
   * Writes model state back to DTOs.
   * Should be called after every calculation pass.
   */
  syncModelStateToDTOs(): void {
    this.dtoSyncService.updateModelStateToDTOs(this.estimateModel, this.currentConfig);
    const reviewDataObj = this.commonDataService.getCostReviewData();
    const costReviewStatus = reviewDataObj?.status;
    const isAutoUnlockBySystem = reviewDataObj?.reviewObject?.isAutoUnlockBySystem ?? false;
    // Keep re-evaluating while the review is approved/in-process, or while it is currently
    // unlocked by the system, so reverting an unsaved change can restore the original status.
    if (costReviewStatus === CostReviewStatus.Approved || costReviewStatus === CostReviewStatus.InProcess || isAutoUnlockBySystem) {
      this.checkCostReviewAutoUnlock();
    }
  }

  private checkCostReviewAutoUnlock(): void {
    const currentSellPrice = this.estimateModel.sellAs.sellAsSellPrice;
    const currentMargin = this.currentConfig.soldAsGMPercent;
    this.costReviewRecordBuilderService.autoUnlockCostReview(
      currentSellPrice,
      currentMargin,
      CONSTANTS.COST_REVIEW_APPROVAL_CHANGE_PERCENT
    );
  }

  //#region Reactive Recalculation -- Public Handlers

  /**
   * Called when the markup toggle changes.
   * Returns the updated model so the component can patch the form.
   */
  onMarkUpToggleChanged(isMarkUpSelected: boolean): EstimateUiModel {
    // Convert percent values to the new mode's units
    const convertedPercent = isMarkUpSelected
      ? this.estimateCommonFunctions.getMarkUpPercent(this.currentConfig.soldAsGMPercent)
      : this.estimateCommonFunctions.getGrossMarginPercent(this.currentConfig.soldAsGMPercent);
    const convertedTechFeePercent = isMarkUpSelected
      ? this.estimateCommonFunctions.getMarkUpPercent(this.currentConfig.technologyFeeGMPercent)
      : this.estimateCommonFunctions.getGrossMarginPercent(this.currentConfig.technologyFeeGMPercent);
    this.currentConfig = {
      ...this.currentConfig,
      isMarkUpSelected,
      soldAsGMPercent: convertedPercent,
      technologyFeeGMPercent: convertedTechFeePercent
    };
    this.estimateModel.markUpToggle = isMarkUpSelected;
    this.refreshAllDisplayedPercentages();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  onSoldAsGMPercentChanged(newPercent: number): EstimateUiModel {
    this.currentConfig = { ...this.currentConfig, soldAsGMPercent: Number(newPercent ? newPercent : 0) };

    // Entering a margin while in Fixed Price mode switches to Fixed Margin so the
    // sell price is re-derived from the entered margin instead of staying pinned.
    if (this.currentConfig.isFixedPriceSelected === true) {
      this.currentConfig = { ...this.currentConfig, isFixedPriceSelected: false };
      this.estimateModel.sellAs.sellAsPricingMode = SolEstimatePricingMode.Fixed_Margin;
    }

    this.recalculateAllSections();
    return this.estimateModel;
  }

  onPricingModeChanged(mode: SolEstimatePricingMode): EstimateUiModel {
    const wasFixedMargin = this.currentConfig.isFixedPriceSelected === false;
    const pinnedSellPrice = this.currentConfig.isFixedPriceSelected === true ? this.estimateModel.sellAs.sellAsSellPrice : undefined;
    this.estimateModel.sellAs.sellAsPricingMode = mode;
    this.currentConfig.technologyFeeGMPercent = this.currentConfig.technologyFeeGMPercent ?? 30;

    if (!this.applyPricingMode(mode)) {
      return this.estimateModel;
    }
    // Subcontractor flags live on the rows and reach the DTO via syncModelStateToDTOs.
    this.subcontractorService.resetSellPriceLastModifiedFlags(this.estimateModel);

    if (mode === SolEstimatePricingMode.Line_Item && this.isZeroCost()) {
      this.switchToLineItemAtZeroCost(wasFixedMargin);
      return this.estimateModel;
    }

    const returnsFromZeroCostLineItem = mode === SolEstimatePricingMode.Fixed_Margin
      && this.keepsFixedMarginOnReturn && this.isZeroCost();
    this.keepsFixedMarginOnReturn = false;
    if (!returnsFromZeroCostLineItem) {
      this.deriveSoldAsMargin(mode);
    }

    const restoreZeroCostRowMargins = mode === SolEstimatePricingMode.Line_Item ? this.captureZeroCostRowMargins() : undefined;
    this.recalculateAllSections();

    if (restoreZeroCostRowMargins) {
      restoreZeroCostRowMargins();
      this.syncModelStateToDTOs();
    }
    if (mode === SolEstimatePricingMode.Fixed_Margin) {
      this.restorePinnedSellPriceAtZeroCost(pinnedSellPrice);
    }
    return this.estimateModel;
  }

  /** Writes the pricing mode into the config. Returns false for a mode that is not recognised. */
  private applyPricingMode(mode: SolEstimatePricingMode): boolean {
    switch (mode) {
      case SolEstimatePricingMode.Fixed_Price:
        this.currentConfig = { ...this.currentConfig, isFixedPriceSelected: true };
        return true;
      case SolEstimatePricingMode.Fixed_Margin:
        this.currentConfig = { ...this.currentConfig, isFixedPriceSelected: false };
        return true;
      case SolEstimatePricingMode.Line_Item:
        this.currentConfig = { ...this.currentConfig, isFixedPriceSelected: null };
        return true;
      default:
        return false;
    }
  }

  private isZeroCost(): boolean {
    return this.estimateModel.subTotals.cost === CONSTANTS.ZERO;
  }

  /** Nothing can be recalculated at zero cost, and recalculating would change the margins shown. */
  private switchToLineItemAtZeroCost(wasFixedMargin: boolean): void {
    this.estimateModel.sellAs.sellAsSellPrice = CONSTANTS.ZERO;
    this.keepsFixedMarginOnReturn = wasFixedMargin;
    this.notifySellAsSellPriceUpdated(CONSTANTS.ZERO);
    this.syncModelStateToDTOs();
  }

  /**
   * The margin currently shown in Sell-As carries over to the new mode, so the rows keep it instead of
   * having it re-derived. With zero cost there is nothing shown to carry over, so it is derived as before.
   * A hidden project has no costs to derive from, so Fixed Margin starts at 0% instead of the stale margin.
   */
  private deriveSoldAsMargin(mode: SolEstimatePricingMode): void {
    const startsAtZeroMargin = mode === SolEstimatePricingMode.Fixed_Margin && this.commonDataService.getRootNodeHidden();
    let soldAsGMPercent: number;
    if (startsAtZeroMargin) {
      soldAsGMPercent = CONSTANTS.ZERO;
    } else if (this.isZeroCost()) {
      soldAsGMPercent = this.calculateGrossMarginPercent();
    } else {
      const shown = this.estimateModel.sellAs.calculated;
      soldAsGMPercent = this.currentConfig.isMarkUpSelected ? shown.markUpPercentage : shown.grossMarginPercentage;
    }
    this.currentConfig = { ...this.currentConfig, soldAsGMPercent };
  }

  /** Fixed Margin sums the row sell prices, which are 0 at zero cost, but the price pinned in Fixed Price must survive. */
  private restorePinnedSellPriceAtZeroCost(pinnedSellPrice: number | undefined): void {
    if (pinnedSellPrice === undefined || !this.isZeroCost()) {
      return;
    }
    this.estimateModel.sellAs.sellAsSellPrice = pinnedSellPrice;
    this.notifySellAsSellPriceUpdated(pinnedSellPrice);
    this.syncModelStateToDTOs();
  }

  /**
   * Called when the user edits the sell-as sell price input (Fixed Price mode).
   * Sets the new sell price on the model, derives GM%/MU% using the same
   * tax-type-specific formulas as onPricingModeChanged, then recalculates all rows.
   */
  onSellAsSellPriceChanged(newSellPrice: number): EstimateUiModel {
    this.estimateModel.sellAs.sellAsSellPrice = newSellPrice;

    // When sell price changes pricing mode automatically switched to Fixed Price mode.
    this.currentConfig = { ...this.currentConfig, isFixedPriceSelected: true };
    this.estimateModel.sellAs.sellAsPricingMode = SolEstimatePricingMode.Fixed_Price;

    //Calculate the technologyFee cost first as it is required to calculate the updated GM% in case of Fixed Price mode.
    this.technologyFeeService.recalculateTechnologyFeeRow(this.estimateModel, this.currentConfig);
    this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);

    const derivedPercent = this.calculateGrossMarginPercent();
    this.currentConfig = { ...this.currentConfig, soldAsGMPercent: derivedPercent };
    this.recalculateAllSections();
    return this.estimateModel;
  }

  onMaterialRowPercentChanged(
    category: 'siemensMaterial' | 'outsideMaterial' | 'mechanicalMaterial' | 'electricalMaterial',
    subRow: 'headerRow' | 'shippingAndHandling' | 'warranty',
    newPercent: number,
    isGMPercent: boolean
  ): EstimateUiModel {
    this.materialService.onMaterialRowPercentChanged(this.estimateModel, this.currentConfig, category, subRow, newPercent, isGMPercent);
    this.recalculateDependentSections();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  /**
   * Called when the user edits a material row's sell price (Line Item mode only).
   * Derives GM% and MU%, recalculates dependent sections (tax, totals, sell-as),
   * then syncs model state back to DTOs.
   */
  onMaterialRowSellPriceChanged(
    category: 'siemensMaterial' | 'outsideMaterial' | 'mechanicalMaterial' | 'electricalMaterial',
    subRow: 'headerRow' | 'shippingAndHandling' | 'warranty',
    newSellPrice: number
  ): EstimateUiModel {
    this.materialService.onMaterialRowSellPriceChanged(this.estimateModel, this.currentConfig, category, subRow, newSellPrice);
    this.recalculateDependentSections();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  onLaborRowPercentChanged(
    laborKey: Exclude<keyof EstimateLaborCostsModel, 'laborCostsHeader'>,
    newPercent: number,
    isGMPercent: boolean
  ): EstimateUiModel {
    this.laborService.onLaborRowPercentChanged(this.estimateModel, this.currentConfig, laborKey, newPercent, isGMPercent);
    this.recalculateDependentSections();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  /**
   * Called when the user edits a labor row's sell price (Line Item mode only).
   * Derives row percentages, recalculates dependent sections (tax, totals, sell-as),
   * then syncs model state back to DTOs.
   */
  onLaborRowSellPriceChanged(
    laborKey: Exclude<keyof EstimateLaborCostsModel, 'laborCostsHeader'>,
    newSellPrice: number
  ): EstimateUiModel {
    this.laborService.onLaborRowSellPriceChanged(this.estimateModel, laborKey, newSellPrice, this.currentConfig);
    this.recalculateDependentSections();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  onSubContractorRowPercentChanged(
    rowIndex: number,
    newPercent: number,
    isGMPercent: boolean
  ): EstimateUiModel {
    this.subcontractorService.onSubContractorRowPercentChanged(this.estimateModel, this.currentConfig, rowIndex, newPercent, isGMPercent);
    this.recalculateDependentSections();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  /**
   * Called when the user edits a subcontractor row's sell price (Line Item mode only).
   * Derives row percentages, recalculates dependent sections (tax, totals, sell-as),
   * then syncs model state back to DTOs.
   */
  onSubContractorRowSellPriceChanged(
    rowIndex: number,
    newSellPrice: number
  ): EstimateUiModel {
    const config = this.currentConfig;
    this.subcontractorService.onSubContractorRowSellPriceChanged(this.estimateModel, rowIndex, newSellPrice, config);
    this.recalculateDependentSections();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  onMiscRiskTravelRowPercentChanged(
    rowName: string,
    newPercent: number,
    isGMPercent: boolean
  ): EstimateUiModel {
    this.miscRiskTravelService.onMiscRiskTravelRowPercentChanged(this.estimateModel, this.currentConfig, rowName, newPercent, isGMPercent);
    this.recalculateDependentSections();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  /**
   * Called when the user edits a misc/risk/travel row's sell price (Line Item mode only).
   * Derives row percentages, recalculates dependent sections (tax, totals, sell-as),
   * then syncs model state back to DTOs.
   */
  onMiscRiskTravelRowSellPriceChanged(
    rowName: string,
    newSellPrice: number
  ): EstimateUiModel {
    this.miscRiskTravelService.onMiscRiskTravelRowSellPriceChanged(this.estimateModel, this.currentConfig, rowName, newSellPrice);
    this.recalculateDependentSections();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  onStandaloneRowPercentChanged(
    rowKey: 'technologyFee' | 'taxNotIncluded',
    newPercent: number,
    isGMPercent: boolean
  ): EstimateUiModel {
    if (rowKey === 'technologyFee') {
      const { updatedConfig } = this.technologyFeeService.onTechnologyFeePercentChanged(
        this.estimateModel, this.currentConfig, newPercent, isGMPercent
      );
      this.currentConfig = updatedConfig;
      this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);
      // Fixed Price: sell price is pinned, so a larger tech fee has to be absorbed by
      // re-deriving the sold-as margin and re-pricing every row with it.
      if (this.currentConfig.isFixedPriceSelected === true) {
        this.currentConfig = {
          ...this.currentConfig,
          soldAsGMPercent: this.calculateGrossMarginPercent()
        };
        this.recalculateAllSections();
        return this.estimateModel;
      }
    } else {
      // taxNotIncluded — standard row calculation
      const isMarkUp = !isGMPercent;
      const row = this.estimateModel.taxNotIncluded;
      const cost = row.cost;
      const result = this.estimateCommonFunctions.calculateRowSellPrice(cost, newPercent, isMarkUp);
      this.estimateCommonFunctions.updateRowResult(row, result, this.estimateModel.subTotals.cost);
    }
    this.recalculateSellAsSellPriceAndNotify();
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  /**
   * Called when the user edits a tax rate input field.
   * Updates the metadata with the new rate, then recalculates tax costs
   * and all dependent sections.
   */
  onTaxRateValueChanged(rowIndex: number, newRate: number): EstimateUiModel {
    this.taxService.updateTaxRateInMetadata(rowIndex, newRate);
    this.recalculateTaxAndMisc();
    this.technologyFeeService.recalculateTechnologyFeeRow(this.estimateModel, this.currentConfig);
    this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);
    if (this.currentConfig.isFixedPriceSelected === true) {
      this.reapplyPinnedSellPriceMargin();
    } else {
      this.recalculateSellAsSellPriceAndNotify();
    }
    this.syncModelStateToDTOs();
    return this.estimateModel;
  }

  recalculateFromMetaDataChange(): EstimateUiModel {
    // Refresh settings-driven values from current metadata (properties may have changed them)
    const updatedTaxTypeID = this.commonDataService.getEstimateMetaData()?.estimateSettings?.taxTypeID ?? this.currentConfig?.taxTypeID;
    this.currentConfig = {
      ...this.currentConfig,
      taxTypeID: updatedTaxTypeID,
      techFeePercent: this.resolveTechFeePercent()
    };
    this.taxService.updateTaxVisibility(updatedTaxTypeID);

    this.recalculateTaxAndMisc();
    this.technologyFeeService.recalculateTechnologyFeeRow(this.estimateModel, this.currentConfig);
    this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);
    if (this.currentConfig.isFixedPriceSelected === true) {
      this.reapplyPinnedSellPriceMargin();
    } else {
      this.recalculateSellAsSellPriceAndNotify();
    }
    // Skip syncModelStateToDTOs here — it calls setOtherMetaData which would
    // re-emit otherMetaDataChanged$ and cause an infinite loop.
    return this.estimateModel;
  }

  //#endregion

  //#region Reactive Recalculation -- Private Helpers

  public recalculateAllSections(): void {
    this.recalculateAllSectionsWithoutSync();
    this.syncModelStateToDTOs();
  }

  private recalculateAllSectionsWithoutSync(): void {
    this.materialService.calculateMaterialSellPrices(this.estimateModel, this.currentConfig);
    this.laborService.recalculateLaborSellPrices(this.estimateModel, this.currentConfig);
    this.subcontractorService.recalculateSubContractorSellPrices(this.estimateModel, this.currentConfig);

    this.recalculateTaxAndMisc();

    this.technologyFeeService.recalculateTechnologyFeeRow(this.estimateModel, this.currentConfig);
    this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);
    this.recalculateSellAsSellPriceAndNotify();
  }

  /**
   * Fixed Price pins the sell price, so a tax change alters the cost and the GM% has to be
   * re-derived and pushed back to every row. Callers must have refreshed tax, tech fee and sub-totals first.
   */
  private reapplyPinnedSellPriceMargin(): void {
    const derivedPercent = this.calculateGrossMarginPercent();
    this.currentConfig = { ...this.currentConfig, soldAsGMPercent: derivedPercent };
    this.recalculateAllSectionsWithoutSync();
  }

  /**
   * Re-runs the full cascade after escalation rates were replaced outside the estimate screen
   * (properties date change, CRM sync, financial-year reset).
   *
   * @returns null when the model isn't configured yet, so callers don't patch an empty model.
   */
  recalculateFromEscalationReset(): void {
    const escalationRates = this.commonDataService.getEstimateMetaData()?.escalationRates;
    if (!this.currentConfig || !escalationRates) {
      return;
    }

    for (const mapping of this.getMaterialEscalationMappings(escalationRates)) {
      if (mapping.escalationData) {
        this.applyEscalationToRow(mapping.escalationData, mapping.modelRow);
        this.materialService.recalculateShippingAndWarrantyForCategory(mapping.materialType, mapping.category);
      }
    }

    for (const mapping of this.getLaborEscalationMappings(escalationRates)) {
      if (mapping.escalationData) {
        this.applyEscalationToRow(mapping.escalationData, mapping.modelRow);
      }
    }

    this.recalculateAllSectionsWithoutSync();
    if (this.currentConfig.isFixedPriceSelected === true) {
      this.reapplyPinnedSellPriceMargin();
    }
    this.syncCostsToProject();
    this.syncModelStateToDTOs();
  }

  /** Calculates and returns either Gross Margin % or Mark Up % based on current configuration. */
  private calculateGrossMarginPercent(): number {
    const calculatedGM = this.sellAsService.getCalculatedGMValues(
      this.estimateModel,
      this.currentConfig,
      this.estimateModel.sellAs.sellAsSellPrice
    );

    return this.currentConfig.isMarkUpSelected
      ? calculatedGM.markUpPercentage
      : calculatedGM.grossMarginPercentage;
  }

  private recalculateDependentSections(): void {
    this.recalculateTaxAndMisc();
    this.technologyFeeService.recalculateTechnologyFeeRow(this.estimateModel, this.currentConfig);
    this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);
    this.recalculateSellAsSellPriceAndNotify();
  }

  /**
   * Used after a cost changed on the estimate screen (shipping, warranty, escalation).
   * Fixed Price pins the sell price, so the new cost has to change the GM% pushed to every row;
   * the other modes just roll the new cost up. The edited costs are also written back to the
   * project so a later re-patch from it (e.g. after the properties panel saves) doesn't revert them.
   */
  private recalculateAfterCostChange(): void {
    this.recalculateTaxAndMisc();
    this.technologyFeeService.recalculateTechnologyFeeRow(this.estimateModel, this.currentConfig);
    this.subtotalsService.recalculateSubTotals(this.estimateModel, this.currentConfig);
    if (this.currentConfig.isFixedPriceSelected === true) {
      this.reapplyPinnedSellPriceMargin();
    } else {
      this.recalculateSellAsSellPriceAndNotify();
    }
    this.syncCostsToProject();
  }

  /**
   * Shipping, warranty and escalation edits only live in the model until the next cost pipeline run, while
   * patchEstimateModelValues rebuilds the model from the project. Writing the edited material and labor costs
   * back keeps the project in step. A hidden project's model costs are zeroed for display only, so it is skipped.
   */
  private syncCostsToProject(): void {
    const project = this.commonDataService.getSQWebProject();
    if (!project || this.currentConfig?.isEstimateReadOnly || this.commonDataService.getRootNodeHidden()) {
      return;
    }

    const mat = this.estimateModel.materialCosts;
    const materialPairs = [
      [project.siemensMaterialCategory, mat.siemensMaterial],
      [project.outsideMaterialCategory, mat.outsideMaterial],
      [project.mechanicalMaterialCategory, mat.mechanicalMaterial],
      [project.electricalMaterialCategory, mat.electricalMaterial]
    ] as const;
    let totalMaterialCost = CONSTANTS.ZERO;
    for (const [category, material] of materialPairs) {
      totalMaterialCost += material.headerRow.cost;
      if (category) {
        category.sumUpCost = material.headerRow.cost;
        category.shippingHandlingCost = material.shippingAndHandling.cost;
        category.warrantyCost = material.warranty.cost;
      }
    }
    if (project.totalMaterialCategory) {
      project.totalMaterialCategory.sumUpCost = totalMaterialCost;
    }

    const lab = this.estimateModel.laborCosts;
    const laborPairs = [
      [project.projectManagerCategory, lab.projectManagement],
      [project.engineerCategory, lab.engineering],
      [project.networkEngineerCategory, lab.networkEngineering],
      [project.qualificationEngineerCategory, lab.qualificationEngineering],
      [project.cyberSecEngineerCategory, lab.cyberSecurityEngineering],
      [project.energyEngineerCategory, lab.energyEngineering],
      [project.lengEngineerCategory, lab.electricalEngineering],
      [project.graphicsCategory, lab.graphics],
      [project.programmingCategory, lab.programming],
      [project.specialistCategory, lab.specialist],
      [project.ddsCategory, lab.digitalDeploymentSpecialist],
      [project.mechanicalCategory, lab.mechanical],
      [project.electricalCategory, lab.electrical]
    ] as const;
    let totalLaborCost = CONSTANTS.ZERO;
    for (const [category, row] of laborPairs) {
      totalLaborCost += row.cost;
      if (category) {
        category.sumUpCost = row.cost;
      }
    }
    if (project.laborTotalCategory) {
      project.laborTotalCategory.sumUpCost = totalLaborCost;
    }
  }

  private recalculateSellAsSellPriceAndNotify(): void {
    if (this.estimateModel.subTotals.cost !== CONSTANTS.ZERO) {
      this.keepsFixedMarginOnReturn = false;
    }
    this.zeroHiddenProjectAmounts();
    this.sellAsService.recalculateSellAsSellPrice(this.estimateModel, this.currentConfig);
    this.applyZeroCostMargins();
    const currentSellAsSellPrice = this.estimateModel.sellAs.sellAsSellPrice;
    this.notifySellAsSellPriceUpdated(currentSellAsSellPrice || 0);
  }

  /** Every row that carries a cost, including section headers, tax-not-included, technology fee and sub-totals. */
  private getAllCostRows(): EstimateRowModel[] {
    const model = this.estimateModel;
    const mat = model.materialCosts;
    const { laborCostsHeader, ...laborRows } = model.laborCosts;
    const misc = model.miscRiskTravelCosts;
    return [
      mat.materialCostsHeader,
      ...[mat.siemensMaterial, mat.outsideMaterial, mat.mechanicalMaterial, mat.electricalMaterial]
        .flatMap(category => [category.headerRow, category.shippingAndHandling, category.warranty]),
      laborCostsHeader,
      ...Object.values(laborRows),
      model.subContractorCosts.headerRow,
      ...model.subContractorCosts.subContractorsCostsRow,
      misc.headerRow,
      ...misc.rows,
      ...(misc.riskCosts ? [misc.riskCosts.headerRow, ...misc.riskCosts.rows] : []),
      model.taxNotIncluded,
      model.technologyFee,
      model.subTotals
    ];
  }

  /**
   * Zero-cost rows have no sell price to derive a margin from, so a Line Item recalculation would reset
   * them to 0%. Switching mode must not change any margin, so their percentages are captured here.
   */
  private captureZeroCostRowMargins(): () => void {
    const saved = this.getAllCostRows()
      .filter(row => row.cost === CONSTANTS.ZERO)
      .map(row => ({ row, gm: row.grossMarginPercentage, mu: row.markUpPercentage }));
    return () => saved.forEach(({ row, gm, mu }) => {
      row.grossMarginPercentage = gm;
      row.markUpPercentage = mu;
    });
  }

  /**
   * A hidden project contributes nothing, so every cost and sell price is zeroed regardless of what the rows were
   * calculated from. Margins are then set by applyZeroCostMargins (kept in Fixed Margin, 0% in Fixed Price), and
   * the Sell-As sell price is the only amount that survives, and only in Fixed Price.
   */
  private zeroHiddenProjectAmounts(): void {
    const isLineItem = this.currentConfig.isFixedPriceSelected === null;
    if (isLineItem || !this.commonDataService.getRootNodeHidden() || this.currentConfig.isEstimateReadOnly) {
      return;
    }

    const model = this.estimateModel;
    this.getAllCostRows().forEach(row => {
      row.cost = CONSTANTS.ZERO;
      row.tdcPercentage = CONSTANTS.ZERO;
      row.grossMargin = CONSTANTS.ZERO;
      row.sellPrice = CONSTANTS.ZERO;
    });

    for (const taxRate of [model.taxMaterialRate, model.taxMaterialLaborRate]) {
      taxRate.headerRow.cost = CONSTANTS.ZERO;
      taxRate.headerRow.tdcPercentage = CONSTANTS.ZERO;
      taxRate.headerRow.sellPrice = CONSTANTS.ZERO;
      for (const row of taxRate.taxRateRows) {
        row.cost = CONSTANTS.ZERO;
        row.tdcPercentage = CONSTANTS.ZERO;
        row.sellPrice = CONSTANTS.ZERO;
      }
    }
  }

  /**
   * With zero total cost no margin can be derived, so each pricing mode shows a fixed rule instead of stale values:
   *  - Fixed Price:  every row, header, sub-total and Sell-As (calculated + reported) is 0%.
   *  - Fixed Margin: every row, header and sub-total shows the global margin.
   *  - Line Item:    headers and sub-total are 0%; rows keep their own margin. When the project is hidden, Sell-As is 0% too.
   * Technology fee shows its configured margin in every mode. Nothing here touches config or costs.
   */
  private applyZeroCostMargins(): void {
    const model = this.estimateModel;
    const config = this.currentConfig;
    if (model.subTotals.cost !== CONSTANTS.ZERO) {
      return;
    }

    const isFixedMargin = config.isFixedPriceSelected === false;
    const zeroPair = { grossMarginPercentage: CONSTANTS.ZERO, markUpPercentage: CONSTANTS.ZERO };
    const modePair = isFixedMargin
      ? this.estimateCommonFunctions.resolvePercentPair(config.soldAsGMPercent, config.isMarkUpSelected)
      : zeroPair;
    const apply = (row: EstimateRowModel, pair: { grossMarginPercentage: number; markUpPercentage: number }): void => {
      row.grossMarginPercentage = pair.grossMarginPercentage;
      row.markUpPercentage = pair.markUpPercentage;
    };

    const misc = model.miscRiskTravelCosts;
    const headers: EstimateRowModel[] = [
      model.materialCosts.materialCostsHeader,
      model.laborCosts.laborCostsHeader,
      model.subContractorCosts.headerRow,
      misc.headerRow,
      model.subTotals,
      ...(misc.riskCosts ? [misc.riskCosts.headerRow] : [])
    ];
    headers.forEach(row => apply(row, modePair));

    if (config.isFixedPriceSelected !== null) {
      const mat = model.materialCosts;
      const { laborCostsHeader, ...laborRows } = model.laborCosts;
      const detailRows: EstimateRowModel[] = [
        ...[mat.siemensMaterial, mat.outsideMaterial, mat.mechanicalMaterial, mat.electricalMaterial]
          .flatMap(category => [category.headerRow, category.shippingAndHandling, category.warranty]),
        ...Object.values(laborRows),
        ...model.subContractorCosts.subContractorsCostsRow,
        ...misc.rows,
        ...(misc.riskCosts?.rows ?? []),
        model.taxNotIncluded
      ];
      detailRows.forEach(row => apply(row, modePair));
    }

    apply(model.technologyFee, this.estimateCommonFunctions.resolvePercentPair(config.technologyFeeGMPercent, config.isMarkUpSelected));

    const isHiddenLineItem = config.isFixedPriceSelected === null && (this.commonDataService.getRootNodeHidden() || this.isZeroCost());
    if (config.isFixedPriceSelected === true || isHiddenLineItem) {
      model.sellAs.calculated.grossMarginPercentage = CONSTANTS.ZERO;
      model.sellAs.calculated.markUpPercentage = CONSTANTS.ZERO;
      model.sellAs.reported.grossMarginPercentage = CONSTANTS.ZERO;
      model.sellAs.reported.markUpPercentage = CONSTANTS.ZERO;
      model.sellAs.turboGMPercent = CONSTANTS.ZERO;
    }
  }

  /**
   * Recalculates tax and misc/risk/travel in the correct order based on tax type.
   *
   * Use Tax / HST / PST: tax is calculated BEFORE misc so auto risk includes the tax cost.
   * Sales Tax / No Tax: tax is calculated AFTER misc (sales tax depends on sell prices from misc rows).
   */
  private recalculateTaxAndMisc(): void {
    const taxTypeID = this.currentConfig.taxTypeID;
    const isTaxBeforeMisc = taxTypeID === TaxTypes.UseTax
      || taxTypeID === TaxTypes.HST
      || taxTypeID === TaxTypes.PST;

    // Runs first so misc/risk never reads a previous tax type's cost.
    this.taxService.resetInactiveTaxRows(this.estimateModel, taxTypeID);

    if (isTaxBeforeMisc) {
      this.taxService.recalculateTaxCosts(this.estimateModel, this.currentConfig);
    }

    this.miscRiskTravelService.recalculateMiscRiskTravelSellPrices(this.estimateModel, this.currentConfig);

    if (!isTaxBeforeMisc) {
      this.taxService.recalculateTaxCosts(this.estimateModel, this.currentConfig);
    }
  }

  private refreshAllDisplayedPercentages(): void {
    this.materialService.refreshMaterialPercentages(this.estimateModel);
    this.laborService.refreshLaborPercentages(this.estimateModel);
    this.subcontractorService.refreshSubContractorPercentages(this.estimateModel);
    this.miscRiskTravelService.refreshMiscRiskTravelPercentages(this.estimateModel);
    this.technologyFeeService.refreshTechnologyFeePercentages(this.estimateModel);
    this.subtotalsService.refreshSubTotalsPercentages(this.estimateModel);
    // taxNotIncluded refresh
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(this.estimateModel.taxNotIncluded);
  }

}
